import { BaseToolbarButton, FieldArrayTable } from '@cks-internal/react-base';
import { useFieldArray, UseFormReturn } from 'react-hook-form';
import {
  EditActivityUpdateModel,
  FormOfPaymentModel
} from '../../../../../features/activities/editActivity/editActivityForm/schema/editActivitySchema';
import { HotelBookingType } from '../../../../../globalModels/common/hotelBookingType';
import { useState } from 'react';
import { Flex, Text, useDisclosure } from '@chakra-ui/react';
import { VirtualCardTransactionsDialog } from '../../../../../features/activities/virtualCards/VirtualCardTransactionsDialog.tsx';
import { useActivityStatusQuery } from '../../../../../features/activities/api/useActivityStatusQuery';
import { FormOfPaymentCreateButtons } from '../../../../../features/activities/editActivity/editActivityForm/components/formOfPaymentCreateButtons.tsx';
import { useFormOfPaymentFields } from '../../../../../features/activities/editActivity/editActivityForm/hooks/useFormOfPaymentFields.tsx';

type Props = {
  hotelBookingType: HotelBookingType | undefined;
  formMethods: UseFormReturn<Partial<EditActivityUpdateModel>>;
  isDisabled: boolean;
  requireFormOfPayment: boolean;
  activityId: number | undefined;
};

export const FormOfPaymentFieldArray = (props: Props) => {
  const fieldArrayControl = useFieldArray({
    control: props.formMethods.control,
    name: 'FormsOfPayment',
    rules: {
      validate: (formsOfPayment: FormOfPaymentModel[]) => {
        if (props.requireFormOfPayment && formsOfPayment.length === 0) {
          return 'At least one form of payment is required';
        }
        return undefined;
      }
    }
  });

  const viewerDisclosure = useDisclosure();
  const [viewingCardId, setViewingCardId] = useState<string | undefined>(undefined);
  const { formFields } = useFormOfPaymentFields({
    formMethods: props.formMethods,
    isDisabled: props.isDisabled,
    hotelBookingType: props.hotelBookingType
  });
  const activityStatus = useActivityStatusQuery(props.activityId);

  return (
    <>
      <FieldArrayTable
        isDisabled={props.isDisabled}
        fieldButtons={
          props.activityId &&
          activityStatus.data && (
            <FormOfPaymentCreateButtons
              activityId={props.activityId}
              activityStatus={activityStatus.data}
              isDisabled={props.isDisabled}
            />
          )
        }
        children={(value) => {
          return (
            <Flex flexDir={'column'}>
              {value.WexCardId && (
                <Flex gap={'4px'} marginLeft={'4px'} marginBottom={'4px'}>
                  <BaseToolbarButton
                    onClick={() => {
                      window.open(value.ViewUrl, '_blank');
                    }}
                    buttonText='View Card'
                  />
                  <BaseToolbarButton
                    onClick={() => {
                      setViewingCardId(value.WexCardId);
                      viewerDisclosure.onOpen();
                    }}
                    buttonText='View Transactions / Declines'
                  />
                </Flex>
              )}
              {value.CardDescription && (
                <Text fontStyle={'italic'} fontSize={'sm'} padding={'0px 4px 4px 4px'}>
                  Notes: {value.CardDescription}
                </Text>
              )}
            </Flex>
          );
        }}
        layoutProps={{
          inputLayout: {
            Comment: { gridColumn: 3, gridRow: 1 },
            LastFour: { gridRow: 1, gridColumn: 2 },
            FormOfPaymentId: { gridRow: 1, gridColumn: 1 },
            ViewUrl: {},
            WexCardId: {}
          },
          fieldLayout: {
            gridTemplateColumns: 'repeat(3,1fr)',
            gridTemplateRows: '1fr'
          }
        }}
        containerProps={{ w: '100%' }}
        header='Forms Of Payment'
        fieldArrayControl={fieldArrayControl}
        formMethods={props.formMethods}
        fields={formFields}
        arrayProp='FormsOfPayment'
      />
      {props.activityId && (
        <VirtualCardTransactionsDialog
          disclosure={viewerDisclosure}
          activityId={props.activityId}
          viewingCardId={viewingCardId}
        />
      )}
    </>
  );
};
